/*
 * Copyright (c) 2024. World Wide Generation Ltd
 */

import { useEffect, useMemo, useState } from 'react';
import { Button } from 'reactstrap';
import { sdgMap, SDGGoal, SDGTarget } from '../../constants/sdg-data';
import SDGIcon from '@g17eco/molecules/sdg-icon/sdg-icon';
import './styles.scss';
import classNames from 'classnames';
import { TargetSelect } from './TargetSelect';
import { QUESTION } from '@constants/terminology';
import { SimpleTooltip } from '@g17eco/molecules/simple-tooltip';

const SDG_TEXT = `Custom ${QUESTION.PLURAL} do not contribute towards SDG scores, as it is not possible to standardise custom ${QUESTION.PLURAL} across organisations. If you wish to actively contribute to the SDGs please use existing platform ${QUESTION.PLURAL}`;

interface SDGPickerProps {
  selectedCodes: string[];
  includeTargets?: boolean;
  handleUpdate: (codes: string[]) => void;
  classes?: {
    label: string;
  }
}

interface SDGPickerButtonProps {
  isSelected: boolean;
  code: string;
  tooltip?: string;
  onClick: (code: string) => void;
}

export type SelectedTarget = {
  label: JSX.Element,
  value: string,
  goal: SDGGoal,
}

export type SelectedGoal = {
  label: string,
  value: string,
  options: SelectedTarget[],
}

const SDGPickerButton = ({ isSelected, tooltip, code, onClick }: SDGPickerButtonProps) => {
  return (
    <SimpleTooltip text={tooltip}>
      <Button color='link-secondary' onClick={() => onClick(code)}>
        <SDGIcon code={code} className={isSelected ? 'selected' : ''} width={'100%'} />
        {isSelected ? <i className='fa fa-check' /> : null}
      </Button>
    </SimpleTooltip>
  )
}

const getOptionLabel = (target: SDGTarget) => (
  <div>
    {target.code} - {target.shortName}
  </div>
);

const getSelectedGoals = (selectedCodes: string[]) =>
  sdgMap
    .filter((goal) => selectedCodes.includes(goal.code))
    .map((goal) => ({
      label: goal.shortName,
      value: goal.code,
      options: goal.targets.map((target) => ({ goal, label: getOptionLabel(target), value: target.code })),
    }));

const SdgPicker = ({ selectedCodes, handleUpdate, includeTargets, classes }: SDGPickerProps) => {
  const [selectedTargetCodes, setSelectedTargetCodes] = useState<SelectedTarget['value'][]>([]);
  const selectedGoals = useMemo(() => getSelectedGoals(selectedCodes), [selectedCodes]);
  const selectedGoalCodes = useMemo(() => selectedGoals.map(({ value }) => value), [selectedGoals]);

  useEffect(() => {
    const selectedTargetCodes = selectedCodes.filter(code => !selectedGoalCodes.includes(code));
    setSelectedTargetCodes(selectedTargetCodes);
  }, [selectedCodes, selectedGoalCodes]);

  const handleToggleSelect = (code: string) => {
    let newCodes = [...selectedCodes];
    const foundCodeIndex = newCodes.findIndex(c => c === code);
    if (foundCodeIndex > -1) {
      newCodes.splice(foundCodeIndex, 1);
      newCodes = newCodes.filter(newCode => !newCode.startsWith(code));
    } else {
      newCodes.push(code);
    }
    handleUpdate(newCodes);
  }

  const onClick = (code: string) => handleToggleSelect(code);

  const handleOnChange = (values: SelectedTarget['value'][]) => {
    setSelectedTargetCodes(values);
    handleUpdate([...selectedGoalCodes, ...values]);
  };

  return (
    <div className='sdg-picker'>
      <div className={classes?.label}>
        <span>Related Sustainable Development Goals</span>
        <SimpleTooltip className='ml-2' text={SDG_TEXT}>
          <i className='fa-light fa fa-circle-info' color='secondary' />
        </SimpleTooltip>
      </div>
      <div className='mt-2 sdg-goals d-flex align-items-stretch'>
        {sdgMap.map(goal => {
          const isSelected = selectedCodes.includes(goal.code);
          return (
            <div key={`sdg-picker-g-${goal.code}`}>
              <SDGPickerButton tooltip={goal.shortName} onClick={onClick} isSelected={isSelected} code={goal.code} />
            </div>
          )
        })}
      </div>
      {includeTargets && (
        <div className='sdg-targets'>
          {selectedGoals.length > 0 ? (
            <div className='sdg-target-list'>
              <div className={classNames('mt-3 mb-2', classes?.label)}>Related SDG Targets</div>
              <TargetSelect selectedGoals={selectedGoals} selectedTargetCodes={selectedTargetCodes} handleOnChange={handleOnChange} />
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
};
export default SdgPicker;
